/**
 * useSwipeToDismiss - Hook for swipe-to-close gesture on mobile Sheets/Drawers.
 *
 * Detects a swipe on ONE axis (horizontal for side sheets, vertical for
 * top/bottom sheets) and closes the surface when the threshold is reached.
 * Only active on mobile (useIsMobile). No external dependency (Pilier III).
 *
 * Usage:
 *   const swipe = useSwipeToDismiss({ onDismiss: close, direction: 'right' }) // side sheet
 *   const swipe = useSwipeToDismiss({ onDismiss: close, direction: 'up' })    // top sheet
 *   <div ref={swipe.ref} onTouchStart={swipe.onTouchStart} onTouchMove={swipe.onTouchMove} onTouchEnd={swipe.onTouchEnd}>
 */

import { useEffect, useRef, useCallback } from 'react'
import { useIsMobile } from '@/hooks/use-mobile'

export type SwipeDirection = 'left' | 'right' | 'up' | 'down'

interface UseSwipeToDismissOptions {
    /** Called when swipe threshold is reached */
    onDismiss: () => void
    /**
     * Direction of swipe to trigger dismiss. Horizontal ('left'/'right') for
     * side sheets, vertical ('up'/'down') for top/bottom sheets.
     */
    direction: SwipeDirection
    /** Minimum distance in px to trigger dismiss (default: 60) */
    threshold?: number
}

export function useSwipeToDismiss({
    onDismiss,
    direction,
    threshold = 60,
}: UseSwipeToDismissOptions) {
    const isMobile = useIsMobile()
    const startX = useRef(0)
    const startY = useRef(0)
    const current = useRef(0)
    const isDragging = useRef(false)
    const isCrossAxisScroll = useRef(false)
    const elementRef = useRef<HTMLDivElement>(null)

    const isVertical = direction === 'up' || direction === 'down'

    /**
     * Plus proche ancêtre réellement scrollable — même logique que
     * `usePullGesture` plus bas dans ce fichier.
     */
    const findScroller = (el: Element | null): Element | null => {
        let node: Element | null = el
        while (node && node !== document.body && node !== document.documentElement) {
            const oy = getComputedStyle(node).overflowY
            if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight) {
                return node
            }
            node = node.parentElement
        }
        return null
    }

    const onTouchStart = useCallback((e: React.TouchEvent) => {
        if (!isMobile) return
        startX.current = e.touches[0].clientX
        startY.current = e.touches[0].clientY
        current.current = 0
        isDragging.current = false
        isCrossAxisScroll.current = false

        // ⚠️ GARDE DE DÉFILEMENT, indispensable sur un geste VERTICAL.
        //
        // Sans elle, remonter une liste à l'intérieur du panneau le refermerait :
        // le doigt va vers le bas dans les deux cas. C'est la raison pour laquelle
        // le geste vertical n'avait pas été câblé — la régression aurait été pire
        // que le défaut. Le geste ne s'arme donc que si le contenu touché est
        // DÉJÀ au bord vers lequel on tire.
        if (isVertical) {
            const scroller = findScroller(e.target as Element)
            if (scroller) {
                const atTop = scroller.scrollTop <= 0
                const atBottom =
                    scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1
                isCrossAxisScroll.current = direction === 'down' ? !atTop : !atBottom
            }
        }
    }, [isMobile, isVertical, direction])

    const onTouchMove = useCallback((e: React.TouchEvent) => {
        if (!isMobile || isCrossAxisScroll.current) return

        const deltaX = e.touches[0].clientX - startX.current
        const deltaY = e.touches[0].clientY - startY.current
        // Delta along the swipe axis vs the perpendicular (cross) axis.
        const axisDelta = isVertical ? deltaY : deltaX
        const crossDelta = isVertical ? deltaX : deltaY

        // On first significant move, decide if this is a cross-axis scroll or a
        // swipe on our axis. Use a 1.8:1 ratio — only classify as cross-axis when
        // clearly dominant (lets diagonal swipes still count on our axis).
        if (!isDragging.current && Math.abs(deltaY) + Math.abs(deltaX) > 10) {
            if (Math.abs(crossDelta) > Math.abs(axisDelta) * 1.8) {
                isCrossAxisScroll.current = true
                return
            }
        }

        // Only react when moving in the dismiss direction.
        const isCorrectDirection =
            direction === 'right' ? deltaX > 0
                : direction === 'left' ? deltaX < 0
                    : direction === 'down' ? deltaY > 0
                        : deltaY < 0 // 'up'

        if (isCorrectDirection && Math.abs(axisDelta) > 10) {
            isDragging.current = true
            current.current = axisDelta

            if (elementRef.current) {
                elementRef.current.style.transform = isVertical
                    ? `translateY(${axisDelta}px)`
                    : `translateX(${axisDelta}px)`
                elementRef.current.style.transition = 'none'
                // Fade proportionally to give visual feedback.
                const progress = Math.min(Math.abs(axisDelta) / (threshold * 1.5), 1)
                elementRef.current.style.opacity = `${1 - progress * 0.3}`
            }
        }
    }, [isMobile, direction, threshold, isVertical])

    const onTouchEnd = useCallback(() => {
        if (!isMobile || !isDragging.current) return
        isDragging.current = false

        const shouldDismiss = Math.abs(current.current) > threshold

        if (elementRef.current) {
            if (shouldDismiss) {
                // Animate fully out along the axis before unmount.
                const extent = isVertical ? window.innerHeight : window.innerWidth
                const sign = direction === 'right' || direction === 'down' ? 1 : -1
                elementRef.current.style.transform = isVertical
                    ? `translateY(${sign * extent}px)`
                    : `translateX(${sign * extent}px)`
                elementRef.current.style.opacity = '0'
                elementRef.current.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out'
                // Small delay to let the animation play before Radix unmounts.
                setTimeout(onDismiss, 150)
            } else {
                // Snap back.
                elementRef.current.style.transform = ''
                elementRef.current.style.opacity = ''
                elementRef.current.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out'
            }
        } else if (shouldDismiss) {
            onDismiss()
        }

        current.current = 0
    }, [isMobile, threshold, direction, isVertical, onDismiss])

    return {
        ref: elementRef,
        onTouchStart,
        onTouchMove,
        onTouchEnd,
    }
}

interface UsePullGestureOptions {
    /** Whether the gesture is currently active (mutually-exclusive open vs close) */
    enabled: boolean
    /** 'down' = pull down to reveal ; 'up' = pull up to dismiss */
    direction: 'down' | 'up'
    /** Called once when the pull crosses the threshold */
    onTrigger: () => void
    /** Minimum distance in px to trigger (default: 64) */
    threshold?: number
}

/**
 * usePullGesture — geste tactile GLOBAL (niveau document) façon « Spotlight » iOS,
 * pour révéler / fermer un panneau plein écran depuis N'IMPORTE OÙ sur l'écran.
 *
 *   • direction 'down' : tirer vers le BAS quand la page est déjà EN HAUT → révèle.
 *   • direction 'up'   : tirer vers le HAUT quand le panneau (sa liste) est en haut → ferme.
 *
 * Ne vole JAMAIS le scroll : ne s'arme que si le conteneur scrollable SOUS le doigt
 * est déjà au bord haut (scrollTop ≤ 0) ; sinon le mouvement reste un scroll normal.
 * Listeners PASSIFS (aucun preventDefault → zéro jank). Mobile uniquement
 * (`useIsMobile`). `enabled` rend l'ouverture et la fermeture mutuellement exclusives.
 */
export function usePullGesture({
    enabled,
    direction,
    onTrigger,
    threshold = 64,
}: UsePullGestureOptions) {
    const isMobile = useIsMobile()
    const onTriggerRef = useRef(onTrigger)
    onTriggerRef.current = onTrigger

    useEffect(() => {
        if (!enabled || !isMobile) return

        let startX = 0
        let startY = 0
        let armed = false
        let scroller: Element | Window | null = null

        const scrollTopOf = (s: Element | Window | null): number =>
            s === null ? 0 : s === window ? window.scrollY : (s as Element).scrollTop

        // Plus proche ancêtre réellement scrollable (sinon la fenêtre).
        const findScroller = (el: Element | null): Element | Window => {
            let node: Element | null = el
            while (node && node !== document.body && node !== document.documentElement) {
                const oy = getComputedStyle(node).overflowY
                if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight) {
                    return node
                }
                node = node.parentElement
            }
            return window
        }

        const onStart = (e: TouchEvent) => {
            if (e.touches.length !== 1) {
                armed = false
                return
            }
            const touch = e.touches[0]
            startX = touch.clientX
            startY = touch.clientY
            // Ouverture : n'armer que si le geste part de la moitié HAUTE de l'écran
            // (évite le geste système « home » en bas). Fermeture : sans restriction.
            if (direction === 'down' && startY > window.innerHeight * 0.6) {
                armed = false
                return
            }
            scroller = findScroller(e.target as Element | null)
            armed = scrollTopOf(scroller) <= 0
        }

        const onMove = (e: TouchEvent) => {
            if (!armed || e.touches.length !== 1) return
            const touch = e.touches[0]
            const dy = touch.clientY - startY
            const dx = touch.clientX - startX
            // Geste clairement vertical uniquement.
            if (Math.abs(dx) > Math.abs(dy) * 0.8) {
                armed = false
                return
            }
            const progress = direction === 'down' ? dy : -dy
            // Toujours au bord haut au déclenchement (sinon = scroll normal).
            if (progress > threshold && scrollTopOf(scroller) <= 0) {
                armed = false
                onTriggerRef.current()
            }
        }

        const onEnd = () => {
            armed = false
        }

        document.addEventListener('touchstart', onStart, { passive: true })
        document.addEventListener('touchmove', onMove, { passive: true })
        document.addEventListener('touchend', onEnd, { passive: true })
        document.addEventListener('touchcancel', onEnd, { passive: true })
        return () => {
            document.removeEventListener('touchstart', onStart)
            document.removeEventListener('touchmove', onMove)
            document.removeEventListener('touchend', onEnd)
            document.removeEventListener('touchcancel', onEnd)
        }
    }, [enabled, isMobile, direction, threshold])
}
